import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UserPlus, Loader2, Phone, Users } from "lucide-react";
import { useTheme } from "../../../contexts/ThemeContext";


const AddGroupModal = ({
  members,
  onClose,
  onSubmit,
  loading,
  selectedMember,
  setSelectedMember,
  groupIdToAdd,
  setGroupIdToAdd,
}) => {
  const { theme } = useTheme();
  const [inputNumber, setInputNumber] = useState("");
  const [formattedNumber, setFormattedNumber] = useState("");
  const noMembers = members.length === 0;

  useEffect(() => {
    if (inputNumber) {
      const formatted = `91${inputNumber.replace(/[^0-9]/g, "")}@c.us`;
      setFormattedNumber(formatted);
    } else {
      setFormattedNumber("");
    }
  }, [inputNumber]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className={`${theme.contentBg} ${theme.contentBorder}  rounded-xl shadow-2xl w-full max-w-md`}
        >
          {/* Header */}
          <div
            className={`${theme.grpBG} ${theme.headerBorder} border-b px-6 py-4 rounded-t-xl`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <UserPlus className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${theme.headerText}`}>
                    Add Member to Group
                  </h3>
                  <p className={`text-xs ${theme.headerText} opacity-75`}>
                    Add a new member to your group
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className={`${theme.sidebarText} opacity-60 hover:opacity-100 transition-opacity`}
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Group ID Input */}
            <div className="space-y-2">
              <label className={`text-sm font-medium ${theme.sidebarText}`}>
                Group ID
              </label>
              <motion.input
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                type="text"
                value={groupIdToAdd}
                onChange={(e) => setGroupIdToAdd(e.target.value)}
                placeholder="Enter the group ID where you are the owner"
                className={`w-full p-3 border ${theme.sidebarBorder} rounded-lg ${theme.sidebarBg} ${theme.sidebarText} ${theme.inputFocusRing} focus:border-transparent focus:ring-2 transition-all duration-200`}
              />
            </div>

            {/* No Members Warning */}
            {noMembers && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    No members available to add to the group.
                  </span>
                </div>
              </motion.div>
            )}

            {/* Member Selection */}
            {!noMembers && (
              <div className="space-y-2">
                <label className={`text-sm font-medium ${theme.sidebarText}`}>
                  Select Member
                </label>
                <motion.select
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  value={selectedMember || ""}
                  onChange={(e) => setSelectedMember(e.target.value)}
                  className={`w-full p-3 border ${theme.sidebarBorder} rounded-lg ${theme.sidebarBg} ${theme.sidebarText} ${theme.inputFocusRing} focus:border-transparent focus:ring-2 transition-all duration-200`}
                >
                  <option value="">Select a member</option>
                  {members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name || "Unnamed"}
                    </option>
                  ))}
                </motion.select>
              </div>
            )}

            {/* Phone Number Input */}
            <div className="space-y-2">
              <label className={`text-sm font-medium ${theme.sidebarText}`}>
                Or Enter Phone Number
              </label>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="relative"
              >
                <Phone
                  className={`absolute left-3 top-3 w-4 h-4 ${theme.sidebarText} opacity-50`}
                />
                <input
                  type="text"
                  value={inputNumber}
                  onChange={(e) => setInputNumber(e.target.value)}
                  placeholder="Enter phone number without country code"
                  className={`w-full pl-10 p-3 border ${theme.sidebarBorder} rounded-lg ${theme.sidebarBg} ${theme.sidebarText} ${theme.inputFocusRing} focus:border-transparent focus:ring-2 transition-all duration-200`}
                  maxLength={10}
                />
              </motion.div>
              {inputNumber && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`text-xs ${theme.sidebarText} opacity-75`}
                >
                  Formatted: {formattedNumber}
                </motion.p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className={`${theme.sidebarBg} ${theme.sidebarBorder} border-t px-6 py-4 rounded-b-xl`}
          >
            <div className="flex justify-end space-x-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className={`px-4 py-2 ${theme.sidebarText} opacity-60 hover:opacity-100 transition-opacity rounded-lg text-sm font-medium`}
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSubmit(formattedNumber || selectedMember)}
                disabled={
                  loading ||
                  (!selectedMember && !formattedNumber) ||
                  !groupIdToAdd ||
                  noMembers
                }
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Add Member</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddGroupModal;

// import React, { useEffect, useState } from 'react';

// const AddGroupModal = ({
//   members,
//   onClose,
//   onSubmit,
//   loading,
//   selectedMember,
//   setSelectedMember,
//   groupIdToAdd,
//   setGroupIdToAdd
// }) => {
//   const [inputNumber, setInputNumber] = useState(''); // State for user's typed number
//   const [formattedNumber, setFormattedNumber] = useState(''); // State for formatted number (for @c.us)

//   // Check if there are no members
//   const noMembers = members.length === 0;

//   // Handle the number input and auto-format to '91<userInput>@c.us'
//   useEffect(() => {
//     if (inputNumber) {
//       const formatted = `91${inputNumber.replace(/[^0-9]/g, '')}@c.us`; // Remove non-numeric characters
//       setFormattedNumber(formatted);
//     } else {
//       setFormattedNumber('');
//     }
//   }, [inputNumber]);

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
//       <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
//         <h3 className="text-2xl font-semibold text-indigo-600 mb-4">Add Member to Group</h3>

//         {/* Group ID Input */}
//         <div>
//           <label className="block text-lg text-gray-700 mb-2">Enter Group ID</label>
//           <input
//             type="text"
//             value={groupIdToAdd}
//             onChange={(e) => setGroupIdToAdd(e.target.value)}
//             className="w-full p-2 border border-gray-300 rounded-lg mb-4"
//             placeholder="Enter the group ID in which you are the Owner"
//           />
//         </div>

//         {/* Check if there are no members */}
//         {noMembers && (
//           <p className="text-red-600 text-sm mb-4">No members available to add to the group.</p>
//         )}

//         {/* Member Selection */}
//         {!noMembers && (
//           <div>
//             <label className="block text-lg text-gray-700 mb-2">Select Member</label>
//             <select
//               value={selectedMember || ''}
//               onChange={(e) => setSelectedMember(e.target.value)}
//               className="w-full p-2 border border-gray-300 rounded-lg mb-4"
//             >
//               <option value="">Select a member</option>
//               {members.map((member) => (
//                 <option key={member.id} value={member.id}>
//                   {member.name || 'Unnamed'}
//                 </option>
//               ))}
//             </select>
//           </div>
//         )}

//         {/* Phone Number Input */}
//         <div>
//           <label className="block text-lg text-gray-700 mb-2">Or Enter Phone Number</label>
//           <input
//             type="text"
//             value={inputNumber}
//             onChange={(e) => setInputNumber(e.target.value)}
//             className="w-full p-2 border border-gray-300 rounded-lg mb-4"
//             placeholder="Enter the phone number without Country Code"
//             maxLength={10} // Limit to 10 digits
//           />
//         </div>

//         {/* Display formatted number */}
//         {inputNumber && (
//           <p className="text-sm text-gray-600">Formatted: {formattedNumber}</p>
//         )}

//         {/* Action Buttons */}
//         <div className="flex justify-end space-x-4">
//           <button
//             onClick={onClose}
//             className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={() => onSubmit(formattedNumber || selectedMember)}
//             className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
//             disabled={loading || (!selectedMember && !formattedNumber) || !groupIdToAdd || noMembers}
//           >
//             {loading ? (
//               <div className="animate-spin border-t-4 border-white rounded-full w-6 h-6 mx-auto"></div>
//             ) : (
//               'Add Member'
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddGroupModal;
